import React from 'react';
import styled from 'styled-components';
import { Placement } from 'popper.js';
import _isUndefined from 'lodash/isUndefined';
import _size from 'lodash/size';
import { useTalentAge } from '@blocs.common';
import { useTranslation } from '@blocs.i18n';
import UncontrolledTooltip from '@minecraft.uncontrolled-tooltip';
import FormattedTalentUnions from '@minecraft.formatted-talent-unions';
import Button from '@minecraft.button';
import Icon from '@minecraft.icon';
import { media } from '@minecraft.themes';
import calculateTalentAge from '../../../shared/utils/calculateTalentAge';
import { useRepShortCode } from '../../../shared/hooks/useRepShortCode';

const TEXT_MAX_LENGTH = 25;
const STAGE_NAME_MAX_LENGTH = 24;
const STAGE_NAME_MIN_LENGTH = 21;

const Wrapper = styled.div`
  align-self: flex-start;
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  margin-left: -0.5rem;
  margin-right: -0.5rem;
  width: calc(100% + 1rem);
`;

const StageName = styled.div<any>`
  font-size: ${(props) =>
    (!props.minor && props?.name?.length > 18) || (props.minor && props?.name?.length > 15) ? '0.75rem' : '0.875rem'};
  font-weight: 600;
`;

const InfoText = styled.div<{ $isFastCastProfile?: boolean }>`
  font-size: 0.75rem;

  ${(props): string =>
    props.$isFastCastProfile &&
    `
    color: ${props.theme.colors.primary.ltGrey1}
  `}
`;

const ProfileAge = styled.span`
  font-size: 0.875rem;
  margin-left: 0;
  display: block;

  ${media.md`
    margin-left: 0.625rem;
    display: inline-block;
  `};
`;

const AgeIcon = styled(Icon)`
  width: 0.5rem;
  height: 0.5rem;
  margin-right: 0.125rem;
  vertical-align: baseline;
`;

const StyledEditRoleButton = styled(Button)`
  font-size: 0.75rem;
  padding: 0;
  min-width: auto;
  height: auto;
  vertical-align: baseline;
  &,
  &:hover {
    color: ${(props): string => props.theme.colors.primary.primary};
    text-decoration: none;
  }
`;

const truncateText = (text, size = TEXT_MAX_LENGTH) => {
  const isTruncated = _size(text) > size;
  const truncatedText = isTruncated ? `${text.substring(0, size)}...` : text;

  return {
    isTruncated,
    truncatedText,
  };
};

const TalentCardInfo = ({
  skipRepsShortCodes,
  isFastCastProfile,
  isPersonalProfile,
  primaryRoleName,
  profileId,
  profileMinor,
  profileStageName: primaryProfileStageName,
  profileMainDivision,
  profileMainOrganizationName,
  profileMainDivisionName,
  profileTalentUnions,
  profileDateOfBirth,
  tooltipPlacement = 'top-start' as Placement,
  className,
  handleRoleNameEdit,
  renderContactInfo,
}) => {
  const { t } = useTranslation();

  const getRepShortCode = useRepShortCode();
  const getTalentAge = useTalentAge();

  const profileStageName = primaryProfileStageName || t('common:profile.deletedProfile');

  const age = calculateTalentAge(profileDateOfBirth);

  const profileAge = getTalentAge(age);

  const isAgeVisible = profileMinor && !!profileAge;
  const repShortCode =
    getRepShortCode({
      skipRepsShortCodes,
      isFastCastProfile,
      isPersonalProfile,
      organizations: [profileMainDivision],
    }) || '';
  const stageNameMaxLength = profileMinor ? STAGE_NAME_MIN_LENGTH : STAGE_NAME_MAX_LENGTH;
  const { isTruncated: isStageNameTruncated, truncatedText: stageName } = truncateText(
    profileStageName,
    stageNameMaxLength
  );
  const { isTruncated: isShortCodeTruncated, truncatedText: shortCode } = truncateText(repShortCode);
  const { isTruncated: isRoleNameTruncated, truncatedText: truncatedRoleName } = truncateText(primaryRoleName, 16);

  return (
    <Wrapper className={className} data-qa-id="talentCardInfo">
      <StageName minor={profileMinor} name={stageName}>
        <span id={`tooltip${profileId}-stageName`} data-qa-id="talentCard-stageName">
          {stageName}
        </span>
        {isAgeVisible && (
          <ProfileAge>
            <AgeIcon name="spicerack-age" alt={t('common:icon.spicerackAge.alt')} inheritColor />
            <strong>{profileAge}</strong>
          </ProfileAge>
        )}
      </StageName>
      {isStageNameTruncated && (
        <UncontrolledTooltip placement={tooltipPlacement} target={`tooltip${profileId}-stageName`}>
          {profileStageName}
        </UncontrolledTooltip>
      )}
      <InfoText id={`tooltip${profileId}-repShortCode`} $isFastCastProfile={isFastCastProfile}>
        {shortCode}
      </InfoText>
      {!isPersonalProfile && (
        <>
          <UncontrolledTooltip placement={tooltipPlacement} target={`tooltip${profileId}-repShortCode`}>
            {profileMainOrganizationName} {profileMainDivisionName ? `(${profileMainDivisionName})` : ''}
          </UncontrolledTooltip>
          {isShortCodeTruncated && (
            <UncontrolledTooltip placement={tooltipPlacement} target={`tooltip${profileId}-repShortCode`}>
              {repShortCode}
            </UncontrolledTooltip>
          )}
        </>
      )}
      <InfoText id={`tooltip${profileId}-roleName`} data-testid="talent-role-name">
        {truncatedRoleName}{' '}
        {handleRoleNameEdit && (
          <StyledEditRoleButton role="button" color="link" onClick={handleRoleNameEdit}>
            {t('common:button.edit')}
          </StyledEditRoleButton>
        )}
      </InfoText>
      {isRoleNameTruncated && (
        <UncontrolledTooltip placement={tooltipPlacement} target={`tooltip${profileId}-roleName`}>
          {primaryRoleName}
        </UncontrolledTooltip>
      )}
      {!_isUndefined(profileTalentUnions) && (
        <InfoText>
          <FormattedTalentUnions
            profileId={profileId}
            unionsList={profileTalentUnions}
            tooltipPlacement={tooltipPlacement}
          />
        </InfoText>
      )}
      {renderContactInfo && renderContactInfo()}
    </Wrapper>
  );
};

export default TalentCardInfo;
